import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { IdCard, ShieldCheck, Store } from 'lucide-react';
import styles from './LandingPage.module.css';
import { useMobileOptimization } from '../hooks/useMobileOptimization';
import SubscribeWithSurveyNew from '../components/SubscribeWithSurveyNew';
import PublicSiteSkipLink from '../components/marketing/PublicSiteSkipLink';
import PublicSiteHeader from '../components/marketing/PublicSiteHeader';
import PublicSiteFooter from '../components/marketing/PublicSiteFooter';
import { collection, addDoc, serverTimestamp } from '../services/firebaseImports';
import { db } from '../config/firebase';
import { useLocationContext } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useConsent } from '../contexts/ConsentContext';
import Logger from '../services/logger';
import {
  LANDING_SUBSCRIBERS_COLLECTION,
  LANDING_SUBSCRIBER_DISCOUNT_CODE,
  LANDING_SUBSCRIBER_DISCOUNT_PERCENT,
} from '../constants/landingSubscriberPromo';
import { LANDING_VISUAL_THEME as tzTheme } from '../constants/landingVisualTheme';
import {
  LANDING_CANONICAL_ORIGIN,
  LANDING_SEO,
  LANDING_HERO,
  LANDING_TRUST,
  LANDING_PROBLEM,
  LANDING_SOLUTION,
  LANDING_PRODUCT_MODULES,
  LANDING_NETWORK,
  LANDING_SECURITY,
  LANDING_FINAL_CTA,
} from '../constants/landingCopy';
import { getLandingBookDemoMailto } from '../constants/landingPublicContact';
import LandingHeroVisual from '../components/landing/LandingHeroVisual';
import {
  LandingProblemDiagram,
  LandingSolutionHubDiagram,
  LandingNetworkDiagram,
} from '../components/landing/LandingDiagrams';

const PRODUCT_MODULE_ICONS = {
  id: IdCard,
  shield: ShieldCheck,
  store: Store,
} as const;

const landingThemeVars: React.CSSProperties = {
  '--tz-navy': tzTheme.navy,
  '--tz-navyElevated': tzTheme.navyElevated,
  '--tz-navyDeep': tzTheme.navyDeep,
  '--tz-navyCard': tzTheme.navyCard,
  '--tz-cyan': tzTheme.cyan,
  '--tz-cyanBright': tzTheme.cyanBright,
  '--tz-slate': tzTheme.slate,
  '--tz-slateMuted': tzTheme.slateMuted,
  '--tz-cloud': tzTheme.cloud,
  '--tz-white': tzTheme.white,
  '--tz-glassBorder': tzTheme.glassBorder,
  '--tz-glassBg': tzTheme.glassBg,
  '--tz-overlay': tzTheme.overlayDark,
} as React.CSSProperties;

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isConsentLoaded, hasConsent } = useConsent();
  const [performanceMetrics, setPerformanceMetrics] = useState<{
    loadTime: number;
    renderTime: number;
  }>({ loadTime: 0, renderTime: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [showSubscribePopup, setShowSubscribePopup] = useState(false);
  const [email, setEmail] = useState('');
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyAnswers, setSurveyAnswers] = useState({
    name: '',
    phoneNumber: '',
    role: '',
    concern: '',
    tech: '',
  });
  const [surveyStatus, setSurveyStatus] = useState('');
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const productSlides = [
    { caption: 'Dashboard & visibility', image: `${process.env.PUBLIC_URL || ''}/images/dashboard-visibility-slide.png` },
    { caption: 'Load flow & execution', image: `${process.env.PUBLIC_URL || ''}/images/load-flow-execution-slide.png` },
    { caption: 'Compliance & verification', image: `${process.env.PUBLIC_URL || ''}/images/compliance-verification-slide.png` },
    { caption: 'Partner workspace', image: `${process.env.PUBLIC_URL || ''}/images/partner-workspace-slide.png` },
  ];

  // Mobile optimization hooks
  const { networkInfo, batteryInfo, isLowBandwidth, isLowBattery } = useMobileOptimization();
  
  // Auth context
  const { user } = useAuth();
  
  // Location context
  const { location, error: locationError, permissionState, requestLocation, retry } = useLocationContext();

  useEffect(() => {
    // Skip during SSR/pre-render
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return;
    }

    // Detect mobile device
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      setIsMobile(isMobileDevice || window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Skip during SSR/pre-render
    if (typeof window === 'undefined' || typeof performance === 'undefined') {
      return;
    }

    const startTime = performance.now();
    
    const measurePerformance = () => {
      const loadTime = performance.now() - startTime;
      setPerformanceMetrics(prev => ({
        ...prev,
        loadTime: Math.round(loadTime)
      }));
    };

    // Measure initial render
    const renderTime = performance.now() - startTime;
    setPerformanceMetrics(prev => ({
      ...prev,
      renderTime: Math.round(renderTime)
    }));

    // Measure full load time
    window.addEventListener('load', measurePerformance);
    
    return () => {
      window.removeEventListener('load', measurePerformance);
    };
  }, []);

  // Show subscribe popup after 3 seconds for better engagement
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSubscribePopup(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Delay location modal until user has scrolled (so first paint is not blocked)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const SCROLL_THRESHOLD = 200;
    const handleScroll = () => {
      if (window.scrollY >= SCROLL_THRESHOLD) setShowLocationModal(true);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToProductDemo = () => {
    document.getElementById('product-slideshow')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleTopSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setShowSurvey(true);
    setSurveyStatus('');
  };

  const handleSurveyChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setSurveyAnswers({ ...surveyAnswers, [e.target.name]: e.target.value });
  };

  const handleSurveySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSurveyStatus("Submitting...");
    
    try {
      await addDoc(collection(db, LANDING_SUBSCRIBERS_COLLECTION), {
        email,
        name: surveyAnswers.name,
        phoneNumber: surveyAnswers.phoneNumber || null,
        role: surveyAnswers.role,
        concern: surveyAnswers.concern,
        tech: surveyAnswers.tech,
        createdAt: serverTimestamp(),
        discountCode: LANDING_SUBSCRIBER_DISCOUNT_CODE,
        discountPercentage: LANDING_SUBSCRIBER_DISCOUNT_PERCENT,
        emailSent: false,
      });
      
      setSurveyStatus(
        `Thank you! Your ${LANDING_SUBSCRIBER_DISCOUNT_PERCENT}% discount code is ${LANDING_SUBSCRIBER_DISCOUNT_CODE}. Save it for when we launch!`
      );
      
      setTimeout(() => {
        setShowSurvey(false);
        setEmail("");
        setSurveyAnswers({ name: "", phoneNumber: "", role: "", concern: "", tech: "" });
        setSurveyStatus("");
      }, 4000);
      
    } catch (err: any) {
      Logger.error('Survey submission error:', err instanceof Error ? err : new Error(String(err)), { err });
      setSurveyStatus("There was an error saving your submission. Please try again.");
    }
  };

  // Removed duplicate subscription logic - SubscribeWithSurvey handles this

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{LANDING_SEO.title}</title>
        <meta name="title" content={LANDING_SEO.metaTitle} />
        <meta name="description" content={LANDING_SEO.description} />
        <meta name="keywords" content="freight logistics, double brokering prevention software, carrier identity verification platform, COI verification for freight brokers, MC/DOT carrier verification tool, TMS load board integration, fraud detection in freight brokerage, verified carrier network for brokers" />
        <link rel="canonical" href={`${LANDING_CANONICAL_ORIGIN}/`} />
        
        {/* Performance Optimization - Resource Hints */}
        <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
        <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
        <link rel="dns-prefetch" href="https://fonts.gstatic.com" />
        <link rel="dns-prefetch" href="https://snap.licdn.com" />
        <link rel="preload" href="https://fonts.googleapis.com/css2?family=Josefin+Sans:ital,wght@0,100..700;1,100..700&display=swap" as="style" />
        <link rel="preload" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap" as="style" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${LANDING_CANONICAL_ORIGIN}/`} />
        <meta property="og:title" content={LANDING_SEO.ogTitle} />
        <meta property="og:description" content={LANDING_SEO.description} />
        <meta property="og:image" content={`${LANDING_CANONICAL_ORIGIN}/logo.png`} />
        <meta property="og:image:alt" content={LANDING_SEO.ogImageAlt} />
        <meta property="og:site_name" content="Tranzit.io" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`${LANDING_CANONICAL_ORIGIN}/`} />
        <meta name="twitter:title" content={LANDING_SEO.ogTitle} />
        <meta name="twitter:description" content={LANDING_SEO.description} />
        <meta name="twitter:image" content={`${LANDING_CANONICAL_ORIGIN}/logo.png`} />
        <meta name="twitter:image:alt" content={LANDING_SEO.ogImageAlt} />
        
        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Tranzit.io",
            "url": LANDING_CANONICAL_ORIGIN,
            "logo": `${LANDING_CANONICAL_ORIGIN}/logo.png`,
            "description": LANDING_SEO.description,
            "sameAs": [],
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "Customer Service",
              "availableLanguage": "English"
            }
          })}
        </script>
        
        {/* Structured Data - SoftwareApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Tranzit.io",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "150"
            },
            "description": LANDING_SEO.description
          })}
        </script>
      </Helmet>
      
    <div
      className={`${styles.landingContainer} tw-font-landing tw-antialiased`}
      style={landingThemeVars}
    >
      <PublicSiteSkipLink />
      {/* Google Analytics: load only when user has accepted Analytics in cookie/consent selection (Phase 1 roadmap) */}
      {process.env.REACT_APP_GOOGLE_ANALYTICS_ID && isConsentLoaded && hasConsent('analytics') && (
        <>
          <script async src={`https://www.googletagmanager.com/gtag/js?id=${process.env.REACT_APP_GOOGLE_ANALYTICS_ID}`}></script>
          <script dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${process.env.REACT_APP_GOOGLE_ANALYTICS_ID}');
            `
          }} />
        </>
      )}
      <PublicSiteHeader />
      <main id="main-content" className={styles.landingMain} tabIndex={-1}>
        <section
          id="landing-hero"
          className={`${styles.heroSection} tw-relative`}
          aria-labelledby="landing-hero-heading"
        >
          <div
            className={`${styles.heroInner} tw-rounded-2xl tw-border tw-border-white/10 tw-shadow-landing-glow tw-p-6 md:tw-p-10`}
          >
            <div className={styles.heroBackdrop} aria-hidden>
              <LandingHeroVisual variant="backdrop" className={styles.heroBackdropVisual} />
              <div className={styles.heroBackdropScrim} />
            </div>
            <div className={styles.heroForeground}>
              <div className={styles.heroContent}>
                <h1 id="landing-hero-heading">{LANDING_HERO.title}</h1>
                <p>{LANDING_HERO.subtitle}</p>
                <div className={styles.heroActions}>
                  <button type="button" className={styles.ctaButton} onClick={() => navigate('/register')}>
                    Get Started
                  </button>
                  <button type="button" className={styles.ctaButtonSecondary} onClick={scrollToProductDemo}>
                    Request Demo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="landing-trust"
          className={styles.heroRoleStrip}
          aria-label="Who Tranzit serves"
        >
          <div className={styles.trustStripInner}>
            <p className={styles.trustTagline}>{LANDING_TRUST.tagline}</p>
            <div className={styles.heroRoleSegments}>
              <div className={styles.heroRoleItem}>
                <span className={styles.heroRoleLabel}>Brokers</span>
                <span className={styles.heroRoleBenefit}>Vet carriers once, use them forever.</span>
              </div>
              <div className={styles.heroRoleItem}>
                <span className={styles.heroRoleLabel}>Shippers</span>
                <span className={styles.heroRoleBenefit}>Know who&apos;s moving your freight.</span>
              </div>
              <div className={styles.heroRoleItem}>
                <span className={styles.heroRoleLabel}>Carriers</span>
                <span className={styles.heroRoleBenefit}>Get repeat tenders from partners who trust you.</span>
              </div>
            </div>
          </div>
        </section>

        <section
          id="landing-problem"
          className={styles.landingSlotSection}
          aria-labelledby="landing-problem-heading"
        >
          <div className={styles.landingSlotInnerWide}>
            <h2 id="landing-problem-heading" className={styles.landingSlotHeading}>
              {LANDING_PROBLEM.title}
            </h2>
            <p className={styles.problemIntro}>{LANDING_PROBLEM.intro}</p>
            <LandingProblemDiagram className={styles.problemDiagramSlot} />
            <div className={styles.problemCardsGrid}>
              {LANDING_PROBLEM.cards.map((card) => (
                <div key={card.title} className={styles.problemCard}>
                  <h3 className={styles.problemCardTitle}>{card.title}</h3>
                  <p className={styles.problemCardText}>{card.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          id="landing-solution"
          className={styles.productProofSection}
          aria-labelledby="landing-solution-heading"
        >
          <h2 id="landing-solution-heading" className={styles.whyDifferentHeading}>
            {LANDING_SOLUTION.title}
          </h2>
          <div className={styles.solutionHubRow}>
            <div className={styles.solutionHubDiagramCol}>
              <LandingSolutionHubDiagram />
            </div>
            <ul className={styles.solutionHubList}>
              {LANDING_SOLUTION.hubItems.map((item) => (
                <li key={item} className={styles.solutionHubItem}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section
          id="landing-product-modules"
          className={styles.landingProductModulesSection}
          aria-labelledby="landing-product-modules-heading"
        >
          <h2 id="landing-product-modules-heading" className={styles.landingProductModulesIntro}>
            {LANDING_PRODUCT_MODULES.sectionTitle}
          </h2>
          <div className={styles.productModulePlaceholderGrid}>
            {LANDING_PRODUCT_MODULES.modules.map((mod) => {
              const Icon = PRODUCT_MODULE_ICONS[mod.icon];
              return (
                <div key={mod.title} className={styles.productModulePlaceholderCard}>
                  <Icon className={styles.productModuleIcon} size={28} strokeWidth={1.75} aria-hidden />
                  <h3 className={styles.productModuleTitle}>{mod.title}</h3>
                  <p className={styles.productModuleText}>{mod.text}</p>
                </div>
              );
            })}
          </div>
          <div id="how-it-works" className={styles.productSlideshowSubsection}>
            <div id="product-slideshow">
              <h2 className={styles.slideshowHeading}>{LANDING_PRODUCT_MODULES.slideshowHeading}</h2>
              <div className={styles.slideshowWrapper}>
                <div className={styles.slideshow}>
                  <button
                    type="button"
                    className={styles.slideNavButton}
                    onClick={() => setCurrentSlide((prev) => (prev === 0 ? productSlides.length - 1 : prev - 1))}
                    aria-label="Previous slide"
                  >
                    ‹
                  </button>
                  <div className={styles.slideFrame}>
                    {productSlides.map((slide, idx) => (
                      <div
                        key={idx}
                        className={styles.slideItem}
                        style={{ display: idx === currentSlide ? 'block' : 'none' }}
                        aria-hidden={idx !== currentSlide}
                      >
                        {'image' in slide && slide.image ? (
                          <img src={slide.image} alt={slide.caption} className={styles.slideImage} />
                        ) : (
                          <div className={styles.slidePlaceholder} />
                        )}
                        <p className={styles.slideCaption}>{slide.caption}</p>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className={styles.slideNavButton}
                    onClick={() => setCurrentSlide((prev) => (prev === productSlides.length - 1 ? 0 : prev + 1))}
                    aria-label="Next slide"
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* Location Authentication Modal - only show for signed-out users after they've scrolled */}
      {!user && !location && showLocationModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="landing-location-dialog-title"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: tzTheme.overlayDark,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, ${tzTheme.navyElevated} 0%, ${tzTheme.navyCard} 100%)`,
              border: `1px solid ${tzTheme.glassBorder}`,
              borderRadius: '16px',
              padding: '2.5rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 24px 64px rgba(0, 0, 0, 0.45)',
              textAlign: 'center',
              color: tzTheme.cloud,
            }}
          >
            <h2
              id="landing-location-dialog-title"
              style={{
                margin: '0 0 1rem 0',
                fontSize: '1.5rem',
                fontWeight: 800,
                color: tzTheme.white,
              }}
            >
              Location Access Required
            </h2>
            <p
              style={{
                margin: '0 0 1.5rem 0',
                fontSize: '1rem',
                color: tzTheme.slate,
                lineHeight: '1.6',
              }}
            >
              This app requires your location to function properly. Please allow location access when prompted by your browser.
              {locationError && (
                <span style={{ display: 'block', marginTop: '0.5rem', color: '#f87171', fontSize: '0.9rem' }}>
                  {locationError}
                </span>
              )}
            </p>
            <button
              type="button"
              onClick={retry}
              style={{
                background: `linear-gradient(135deg, ${tzTheme.navy} 0%, ${tzTheme.navyCard} 100%)`,
                color: tzTheme.white,
                border: `1px solid ${tzTheme.cyan}66`,
                borderRadius: '8px',
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: `0 4px 24px ${tzTheme.cyan}33`,
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = tzTheme.cyanBright;
                e.currentTarget.style.boxShadow = `0 6px 32px ${tzTheme.cyan}44`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = `${tzTheme.cyan}66`;
                e.currentTarget.style.boxShadow = `0 4px 24px ${tzTheme.cyan}33`;
              }}
            >
              Enable Location
            </button>
            <div
              style={{
                marginTop: '1rem',
                fontSize: '0.85rem',
                color: tzTheme.slateMuted,
                lineHeight: '1.5',
              }}
            >
              If you previously denied location access, please enable it in your browser settings and click the button above.
            </div>
          </div>
        </div>
      )}

        <section
          id="landing-network"
          className={styles.landingSlotSection}
          aria-labelledby="landing-network-heading"
        >
          <div className={styles.landingSlotInner}>
            <h2 id="landing-network-heading" className={styles.landingSlotHeading}>
              {LANDING_NETWORK.title}
            </h2>
            <p className={styles.networkBody}>{LANDING_NETWORK.body}</p>
            <LandingNetworkDiagram className={styles.networkDiagramSlot} />
          </div>
        </section>

        <section
          id="landing-security"
          className={styles.pointsSection}
          aria-labelledby="landing-security-heading"
        >
          <h2 id="landing-security-heading" className={styles.landingSecuritySectionHeading}>
            {LANDING_SECURITY.title}
          </h2>
          <p className={styles.securityIntro}>{LANDING_SECURITY.intro}</p>
          <div className={styles.featuresGrid}>
            {LANDING_SECURITY.cards.map((card) => (
              <div key={card.pillar} className={styles.featureCard}>
                <div className={styles.featurePillar}>{card.pillar}</div>
                <h3 className={styles.featureHeadline}>{card.headline}</h3>
                <p className={styles.featureText}>{card.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          id="landing-final-cta"
          className={styles.ctaStripBeforeFooter}
          aria-label="Get started with Tranzit"
        >
          <p className={styles.ctaStripText}>{LANDING_FINAL_CTA.line}</p>
          <div className={styles.ctaStripActions}>
            <button type="button" className={styles.ctaStripButtonPrimary} onClick={() => navigate('/register')}>
              {LANDING_FINAL_CTA.primaryLabel}
            </button>
            <a className={styles.ctaStripButtonSecondary} href={getLandingBookDemoMailto()}>
              {LANDING_FINAL_CTA.secondaryLabel}
            </a>
          </div>
        </section>
      </main>

      <PublicSiteFooter />

      {/* Survey Modal */}
      {showSurvey && (
        <div className={styles.surveyOverlay} onClick={() => setShowSurvey(false)}>
          <div className={styles.surveyContent} onClick={(e) => e.stopPropagation()}>
            <button 
              className={styles.surveyClose} 
              onClick={() => setShowSurvey(false)}
              aria-label="Close survey"
            >
              ×
            </button>
            <form onSubmit={handleSurveySubmit}>
              <h3 className={styles.surveyTitle}>🎉 Thank you for subscribing!</h3>
              <p className={styles.surveySubtitle}>
                Answer 3 quick questions for a{' '}
                <span className={styles.highlight}>{LANDING_SUBSCRIBER_DISCOUNT_PERCENT}% discount</span> on your first year:
              </p>
              
              <label className={styles.surveyLabel}>
                Full Name *
                <input 
                  name="name" 
                  value={surveyAnswers.name} 
                  onChange={handleSurveyChange} 
                  required 
                  className={styles.surveyInput}
                  placeholder="Enter your full name"
                />
              </label>
              
              <label className={styles.surveyLabel}>
                Phone Number (Optional)
                <input 
                  name="phoneNumber" 
                  value={surveyAnswers.phoneNumber} 
                  onChange={handleSurveyChange} 
                  type="tel"
                  className={styles.surveyInput}
                  placeholder="Enter your phone number"
                />
              </label>
              
              <label className={styles.surveyLabel}>
                Are you a shipper, carrier, broker, or broker/carrier?
                <select name="role" value={surveyAnswers.role} onChange={handleSurveyChange} required className={styles.surveySelect}>
                  <option value="">Select...</option>
                  <option value="shipper">Shipper</option>
                  <option value="carrier">Carrier</option>
                  <option value="broker">Broker</option>
                  <option value="broker_carrier">Broker/Carrier</option>
                </select>
              </label>
              
              <label className={styles.surveyLabel}>
                What is your biggest concern in working directly with your shipper/carrier counterpart?
                <textarea 
                  name="concern" 
                  value={surveyAnswers.concern} 
                  onChange={handleSurveyChange} 
                  required 
                  className={styles.surveyTextarea}
                  placeholder="Tell us about your concerns..."
                />
              </label>
              
              <label className={styles.surveyLabel}>
                What can we do from the tech side to make your operations easier between you and your partner?
                <textarea 
                  name="tech" 
                  value={surveyAnswers.tech} 
                  onChange={handleSurveyChange} 
                  required 
                  className={styles.surveyTextarea}
                  placeholder="Share your ideas..."
                />
              </label>
              
              <button type="submit" className={styles.surveySubmitButton}>Submit for Discount</button>
              
              {surveyStatus && (
                <div className={`${styles.surveyStatus} ${surveyStatus.includes('error') ? styles.error : styles.success}`}>
                  {surveyStatus}
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Subscribe Popup Modal */}
      {showSubscribePopup && (
        <div className={styles.popupOverlay} onClick={() => setShowSubscribePopup(false)}>
          <div className={styles.popupContent} onClick={(e) => e.stopPropagation()}>
            <button 
              className={styles.popupClose} 
              onClick={() => setShowSubscribePopup(false)}
              aria-label="Close popup"
            >
              ×
            </button>
            <div className={styles.popupHeader}>
              <h3 className={styles.popupTitle}>Don't Miss Out!</h3>
              <p className={styles.popupSubtitle}>
                Get exclusive discount + early access
              </p>
            </div>
            <SubscribeWithSurveyNew />
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default LandingPage; 